import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { Avatar, Button, Menu, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PortalPermissions, useAuth_SignOutMutation } from 'queries';
import { useAnalytics } from 'use-analytics';

import { AnalyticsEvent } from 'utils/enum';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useHasPortalPermission from 'utils/useHasPortalPermission';
import { nameToInitials } from 'utils/user';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: 10,
    [theme.breakpoints.down('xs')]: {
      height: 28,
      width: 28,
    },
    background: 'white',
  },
  userName: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  rounded: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 40,
    width: 40,
    borderRadius: '50%',
    background: 'white',
    color: 'black',
    marginRight: 10,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    width: '190px',
  },
  menuItemIcon: {
    marginRight: 8,
  },
}));

const Account = () => {
  const classes = useStyles();
  const history = useHistory();
  const analytics = useAnalytics();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentUser = useCurrentUser();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const [logout] = useAuth_SignOutMutation({
    onCompleted: () => {
      analytics.track(AnalyticsEvent.LOGOUT);
      analytics.reset();

      history.push('/');
    },
    onError: (e) => {
      showAlert({
        message: 'Error signing out: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const avatar = currentUser?.avatarUrl;
  const fullName = `${currentUser?.firstName} ${currentUser?.lastName}`;

  let canAccessAccountPage = hasPortalPermissionAdmin || hasPortalPermissionUser;

  if (currentUser?.isSuperAdmin) {
    // TSA accounts do not have an Accounts page
    canAccessAccountPage = false;
  }

  return (
    <div className={classes.root}>
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownIcon color="inherit" />}
        color="inherit"
      >
        {avatar ? (
          <Avatar alt="Profile Photo" src={avatar} className={classes.avatar} />
        ) : (
          <div className={classes.rounded}>
            {nameToInitials(currentUser?.firstName || '', currentUser?.lastName || '')}
          </div>
        )}
        <Typography color="inherit" className={classes.userName}>
          {fullName}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => history.push(`/home/${currentUser?.defaultPortal?.id}/home`)}>
          <div className={classes.menuItem}>
            <HomeIcon color="inherit" className={classes.menuItemIcon} /> Home
          </div>
        </MenuItem>
        <MenuItem
          onClick={() => history.push(`/home/${currentUser?.defaultPortal?.id}/my-profile`)}
        >
          <PersonIcon color="inherit" className={classes.menuItemIcon} /> Profile
        </MenuItem>
        {canAccessAccountPage && (
          <MenuItem
            onClick={() => {
              history.push(`/home/${currentUser?.defaultPortal?.id}/portal-account`);
            }}
          >
            <SettingsIcon color="inherit" className={classes.menuItemIcon} />
            Account
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            logout();
          }}
        >
          <LogoutIcon color="inherit" className={classes.menuItemIcon} />
          Logout
        </MenuItem>
      </Menu>
    </div>
  );
};

export default Account;
